import {
  authProvider,
  getRateLimitErrorMessage,
  NotificationType,
  SocketEmitEvent,
  SocketEvent,
  useTranslation,
} from "@next-platform/client-common-core";
import { TFunction } from "i18next";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

import { useToast } from "@/hooks";

import useAudioPlayback from "./useAudioPlayback";

const SOCKET_URL = import.meta.env.VITE_SOCKET_SERVER;

interface ISocketHandlers {
  clearIsGenerating: () => void;
  stopAudio: () => void;
  markSocketErrored: () => void;
  displayToast: (args: { message: string; type: NotificationType }) => void;
  t: TFunction;
  scheduleChunk: (audioBytes: number[]) => void;
  setStateIsPlaying: (isPlaying: boolean) => void;
  attachOnEndedToLastSource: () => void;
  refDoneReceived: MutableRefObject<boolean>;
  refPendingDecodes: MutableRefObject<number>;
  refRequestComplete: MutableRefObject<boolean>;
  playFromUrl: (url: string) => void;
}

const setupSocketListeners = (socket: Socket, handlers: ISocketHandlers) => {
  const { t } = handlers;
  const rateLimitErrorMessage = getRateLimitErrorMessage(t);
  let hasChunksReceived = false;
  socket.on("connect_error", () => {
    handlers.refRequestComplete.current = true;
    handlers.clearIsGenerating();
    handlers.stopAudio();
    handlers.markSocketErrored();
    handlers.displayToast({ message: t("error:connectionError"), type: NotificationType.error });
  });
  socket.on("error", (socketError: string) => {
    handlers.refRequestComplete.current = true;
    handlers.clearIsGenerating();
    handlers.stopAudio();
    handlers.markSocketErrored();
    handlers.displayToast({ message: socketError || t("error:fallbackError"), type: NotificationType.error });
  });
  socket.on("rate_limit_reached", () => {
    handlers.refRequestComplete.current = true;
    handlers.clearIsGenerating();
    handlers.stopAudio();
    handlers.markSocketErrored();
    handlers.displayToast({ message: rateLimitErrorMessage.RATE_LIMIT_BREACHED_PLAY_MESSAGE_AUDIO, type: NotificationType.error });
  });
  socket.on("disconnect", () => {
    handlers.clearIsGenerating();
    if (handlers.refDoneReceived.current) return;
    handlers.attachOnEndedToLastSource();
  });
  socket.on(SocketEvent.AUDIO_DATA, (response: string) => {
    const { t } = handlers;
    let parsed: { type: string; firstChunk?: boolean } & Record<string, unknown>;
    try {
      parsed = JSON.parse(response);
    } catch {
      handlers.refRequestComplete.current = true;
      handlers.clearIsGenerating();
      handlers.stopAudio();
      handlers.markSocketErrored();
      handlers.displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
      return;
    }
    if (parsed.type === "audio_url") {
      handlers.refRequestComplete.current = true;
      handlers.clearIsGenerating();
      const audioUrl = parsed["data"];
      if (typeof audioUrl !== "string" || !audioUrl) return;
      handlers.setStateIsPlaying(true);
      handlers.playFromUrl(audioUrl);
    } else if (parsed.type === "chunk") {
      hasChunksReceived = true;
      if (parsed.firstChunk) {
        handlers.clearIsGenerating();
        handlers.setStateIsPlaying(true);
      }
      handlers.scheduleChunk(parsed["data"] as number[]);
    } else if (parsed.type === "done") {
      handlers.refRequestComplete.current = true;
      handlers.clearIsGenerating();
      const audioUrl = parsed["data"];
      if (!hasChunksReceived && typeof audioUrl === "string" && audioUrl) {
        handlers.setStateIsPlaying(true);
        handlers.playFromUrl(audioUrl);
        return;
      }
      handlers.refDoneReceived.current = true;
      if (handlers.refPendingDecodes.current === 0) handlers.attachOnEndedToLastSource();
    }
  });
};

const useVoicePreview = (voiceId?: string) => {
  const refSocket = useRef<Socket | null>(null);
  const refIsGenerating = useRef(false);
  const refRequestComplete = useRef(true);

  const [stateGenerationStatus, setStateGenerationStatus] = useState<"idle" | "generating">("idle");

  const { stopAudio, scheduleChunk, attachOnEndedToLastSource, setStateIsPlaying, refDoneReceived, refPendingDecodes, isPlaying, playFromUrl } =
    useAudioPlayback();

  const clearIsGenerating = useCallback(() => {
    refIsGenerating.current = false;
    setStateGenerationStatus("idle");
  }, []);

  const { displayToast } = useToast();
  const { t } = useTranslation();

  const disconnect = useCallback(() => {
    if (!refSocket.current) return;
    refSocket.current.removeAllListeners();
    refSocket.current.disconnect();
    refSocket.current = null;
  }, []);

  const previewVoice = useCallback(
    async (text: string, overrideVoiceId?: string) => {
      const resolvedVoiceId = overrideVoiceId || voiceId;
      if (refIsGenerating.current || !resolvedVoiceId || !text.trim()) return;
      refIsGenerating.current = true;

      stopAudio();
      setStateGenerationStatus("generating");

      try {
        const shouldCreateNewSocket = !refSocket.current || !refSocket.current.connected || !refRequestComplete.current;

        if (shouldCreateNewSocket) {
          disconnect();
          const token = await authProvider.getToken();
          refSocket.current = io(`${SOCKET_URL}/?token=${token}`, {
            transports: ["websocket", "polling"],
            withCredentials: true,
            reconnection: false,
          });
        } else {
          refSocket.current!.removeAllListeners();
        }

        refRequestComplete.current = false;
        const socket = refSocket.current!;

        setupSocketListeners(socket, {
          clearIsGenerating,
          stopAudio,
          markSocketErrored: disconnect,
          displayToast,
          t,
          scheduleChunk,
          setStateIsPlaying,
          attachOnEndedToLastSource,
          refDoneReceived,
          refPendingDecodes,
          refRequestComplete,
          playFromUrl,
        });

        socket.emit(SocketEmitEvent.PREVIEW_VOICE, resolvedVoiceId, text);
      } catch {
        clearIsGenerating();
      }
    },
    [
      voiceId,
      clearIsGenerating,
      stopAudio,
      disconnect,
      scheduleChunk,
      attachOnEndedToLastSource,
      displayToast,
      t,
      setStateIsPlaying,
      refDoneReceived,
      refPendingDecodes,
      playFromUrl,
    ],
  );

  const stopPreview = useCallback(() => {
    clearIsGenerating();
    stopAudio();
    if (refSocket.current) refSocket.current.removeAllListeners();
  }, [clearIsGenerating, stopAudio]);

  useEffect(() => {
    return () => {
      stopAudio();
      disconnect();
    };
  }, [stopAudio, disconnect]);

  return {
    previewVoice,
    stopPreview,
    isGenerating: stateGenerationStatus === "generating",
    isPlaying,
  };
};

export default useVoicePreview;
